import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Images,
} from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";

import { fetchImage } from "../../api/newsService";
import "./MediaImage.scss";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { TitleComponent } from "../../components/TitleDocument";
import BreadCrumb from "../../components/BreadCrumb";
import RelatedMediaSidebar from "../../components/RelatedMediaSidebar/RelatedMediaSidebar";

const getAlbumImages = (album) => {
  if (!album) return [];

  const contentImages =
    album.content
      ?.split(";")
      .map((url) => url.trim())
      .filter(Boolean) || [];

  if (contentImages.length > 0) {
    return contentImages;
  }

  return album.image ? [album.image] : [];
};

const getAlbumDateLabel = (album) => {
  if (!album) return "";

  return album.publishDate || album.createTime || album.createDate || album.date || "";
};

const AlbumDetailLoading = () => (
  <section className="media_image_detail">
    <TitleComponent title="Chi tiết album" />

    <div className="media_image_detail__crumb container">
      <BreadCrumb />
    </div>

    <div className="media_image_detail__layout container">
      <article className="media_image_detail__main">
        <div className="media_image_detail__loading-card" role="status" aria-live="polite">
          <div className="media_image_detail__loading-screen" />
          <div className="media_image_detail__loading-copy">
            <span />
            <span />
            <p>Đang tải nội dung...</p>
          </div>
        </div>
      </article>

      <aside className="media_image_detail__loading-sidebar" aria-hidden="true">
        <div className="media_image_detail__loading-sidebar-head" />
        {[1, 2, 3, 4, 5].map((item) => (
          <div className="media_image_detail__loading-sidebar-item" key={item}>
            <span />
            <div>
              <i />
              <i />
            </div>
          </div>
        ))}
      </aside>
    </div>
  </section>
);

const MediaImage = () => {
  const { idImage } = useParams();
  const [albums, setAlbums] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});
  const screenRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const getAlbums = async () => {
      try {
        const res = await fetchImage();
        if (isMounted && res?.data?.data?.lstImage?.length > 0) {
          setAlbums(res.data.data.lstImage);
        }
      } catch (error) {
        if (isMounted) {
          setAlbums([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    getAlbums();

    return () => {
      isMounted = false;
    };
  }, []);

  const detail = useMemo(
    () => albums.find((item) => Number(item.id) === Number(idImage)),
    [albums, idImage]
  );

  const albumImages = useMemo(() => getAlbumImages(detail), [detail]);

  const otherAlbums = useMemo(
    () => albums.filter((item) => Number(item.id) !== Number(detail?.id)),
    [albums, detail?.id]
  );

  useEffect(() => {
    setActiveImageIndex(0);
  }, [detail?.id]);

  const markImageLoaded = useCallback((image) => {
    if (!image) return;

    setLoadedImages((current) => {
      if (current[image]) return current;
      return { ...current, [image]: true };
    });
  }, []);

  const activeImage = albumImages[activeImageIndex];

  useEffect(() => {
    if (!activeImage) return;

    const imageElement = Array.from(screenRef.current?.querySelectorAll("img") || []).find(
      (item) => item.currentSrc === activeImage || item.src === activeImage
    );

    if (imageElement?.complete) {
      markImageLoaded(activeImage);
    }
  }, [activeImage, markImageLoaded]);

  if (isLoading) {
    return <AlbumDetailLoading />;
  }

  if (!detail) {
    return (
      <section className="media_image_detail">
        <TitleComponent title="Chi tiết album" />

        <div className="media_image_detail__crumb container">
          <BreadCrumb />
        </div>

        <div className="media_image_detail__empty container">
          <p>Không tìm thấy album.</p>
        </div>
      </section>
    );
  }

  const dateLabel = getAlbumDateLabel(detail);
  const description = detail.description || detail.discription;
  const isActiveImageLoading = Boolean(activeImage && !loadedImages[activeImage]);

  return (
    <section className="media_image_detail">
      <TitleComponent title={detail.title} />

      <div className="media_image_detail__crumb container">
        <BreadCrumb />
      </div>

      <div className="media_image_detail__layout container">
        <article className="media_image_detail__main">
          <div className="media_image_detail__album-card">
            <div className="media_image_detail__screen" ref={screenRef}>
              {albumImages.length > 1 ? (
                <div className="media_image_detail__slider">
                  <button
                    ref={prevRef}
                    type="button"
                    className="media_image_detail__nav media_image_detail__nav--prev"
                    aria-label="Ảnh trước"
                  >
                    <ChevronLeft size={22} />
                  </button>

                  <button
                    ref={nextRef}
                    type="button"
                    className="media_image_detail__nav media_image_detail__nav--next"
                    aria-label="Ảnh tiếp theo"
                  >
                    <ChevronRight size={22} />
                  </button>

                  <Swiper
                    key={detail.id}
                    modules={[Navigation, Pagination]}
                    slidesPerView={1}
                    spaceBetween={0}
                    pagination={{ clickable: true }}
                    navigation={{
                      prevEl: prevRef.current,
                      nextEl: nextRef.current,
                    }}
                    onBeforeInit={(swiper) => {
                      swiper.params.navigation.prevEl = prevRef.current;
                      swiper.params.navigation.nextEl = nextRef.current;
                    }}
                    onSlideChange={(swiper) => setActiveImageIndex(swiper.realIndex)}
                    className="media_image_detail__swiper"
                  >
                    {albumImages.map((image, index) => (
                      <SwiperSlide key={`${detail.id}-${index}`}>
                        <div className="media_image_detail__slide">
                          <img
                            src={image}
                            alt={`${detail.title} ${index + 1}`}
                            loading={index === 0 ? "eager" : "lazy"}
                            decoding="async"
                            onLoad={() => markImageLoaded(image)}
                            onError={() => markImageLoaded(image)}
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              ) : (
                <img
                  src={albumImages[0] || ""}
                  alt={detail.title}
                  loading="eager"
                  decoding="async"
                  onLoad={() => markImageLoaded(albumImages[0])}
                  onError={() => markImageLoaded(albumImages[0])}
                />
              )}

              {isActiveImageLoading ? (
                <div className="media_image_detail__image-loading" role="status" aria-live="polite">
                  <span />
                  <p>Đang tải ảnh...</p>
                </div>
              ) : null}

              <span className="media_image_detail__image-index">
                {albumImages.length > 0 ? `${activeImageIndex + 1} / ${albumImages.length}` : "0 / 0"}
              </span>
            </div>

            <div className="media_image_detail__info">
              <div className="media_image_detail__info-head">
                <h1>{detail.title}</h1>
                <span className="media_image_detail__count">
                  <Images size={16} />
                  {albumImages.length} ảnh trong album
                </span>
              </div>

              {dateLabel && (
                <div className="media_image_detail__date">
                  <CalendarDays size={15} />
                  <span>{dateLabel}</span>
                </div>
              )}

              {description ? <p>{description}</p> : null}
            </div>
          </div>
        </article>

        <RelatedMediaSidebar
          title="Album khác"
          viewAllPath="/thu-vien-hinh-anh"
          items={otherAlbums}
          variant="album"
          maxItems={10}
          getItemPath={(album) => `/thu-vien-hinh-anh/${album.id}`}
          getDateLabel={getAlbumDateLabel}
          getImageCount={(album) => getAlbumImages(album).length}
        />
      </div>
    </section>
  );
};

export default MediaImage;
